import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AuthLayout from "./AuthLayout";
import { api, getErrorMessage } from "@/lib/api";

export default function ResetPassword() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (password !== confirm) return setErr("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/api/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 1500);
    } catch (e2) {
      setErr(getErrorMessage(e2));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      {done ? (
        <div className="text-center" data-testid="reset-done">
          <h2 className="text-[28px] font-bold text-cm-navy">Password reset</h2>
          <p className="mt-3 text-cm-grey text-[14px]">Redirecting you to sign in…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <h2 className="text-[28px] font-bold text-cm-navy">Reset your password</h2>
          <p className="text-cm-grey text-[14px]">Choose a strong new password.</p>
          {!token && (
            <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-[10px] px-4 py-3">
              Reset token missing. Open this page via the link we sent to your email.
            </div>
          )}
          <div>
            <label className="text-[13px] font-semibold text-cm-navy">New password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password"
              className="mt-1.5 w-full px-4 py-3.5 rounded-[10px] border border-[#E0E3EF] outline-none text-cm-navy text-[14px] focus:border-cm-blue transition"
              placeholder="Min 8 chars • Aa1!"
            />
          </div>
          <div>
            <label className="text-[13px] font-semibold text-cm-navy">Confirm password</label>
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm"
              className="mt-1.5 w-full px-4 py-3.5 rounded-[10px] border border-[#E0E3EF] outline-none text-cm-navy text-[14px] focus:border-cm-blue transition"
              placeholder="Re-enter new password"
            />
          </div>
          {err && (
            <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-[10px] px-4 py-3">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !token}
            data-testid="reset-submit"
            className="w-full py-3.5 rounded-[10px] bg-cm-blue hover:bg-cm-blue-deep transition text-white font-semibold text-[15px] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Reset password
          </button>
          <Link
            to="/login"
            className="block text-center text-[13px] text-cm-grey hover:text-cm-blue"
          >
            ← Back to sign in
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}
